import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { DotsThreeVertical, PencilSimple, Trash, ArrowUUpLeft, Scales, Eye } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const LOCATIONS = ["FG-A", "FG-B", "FG-C", "FG-D", "Cold-Store", "Quarantine"];
const ADJ_KINDS = ["adjustment", "damage", "expiry", "count", "wastage"];

function FgEditor({ open, fg, onClose, onDone, readOnly }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (open && fg) setForm({
    qty: fg.qty ?? 0, reserved: fg.reserved ?? 0, location: fg.location || "FG-A",
    value: fg.value ?? 0, mfg_date: fg.mfg_date?.slice(0, 10) || "", exp_date: fg.exp_date?.slice(0, 10) || "",
  }); }, [open, fg]);

  const submit = async () => {
    try {
      await api.put(`/finished-goods/${fg.id}`, {
        ...form,
        qty: parseInt(form.qty) || 0,
        reserved: parseInt(form.reserved) || 0,
        value: parseFloat(form.value) || 0,
      });
      toast.success("Finished goods lot updated");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{readOnly ? "FG Lot Details" : `Edit ${fg?.batch_no}`}</DialogTitle>
          <DialogDescription className="sr-only">FG Lot</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{fg?.sku_name}</span> · <span className="mono">{fg?.sku_code}</span>
          </div>
          <div><Label>Quantity (pouches)</Label><Input type="number" value={form.qty} onChange={e => setForm({...form, qty: e.target.value})} disabled={readOnly} data-testid="fg-qty"/></div>
          <div><Label>Reserved</Label><Input type="number" value={form.reserved} onChange={e => setForm({...form, reserved: e.target.value})} disabled={readOnly}/></div>
          <div><Label>Location</Label>
            <Select value={form.location} onValueChange={v => setForm({...form, location: v})} disabled={readOnly}>
              <SelectTrigger data-testid="fg-location"><SelectValue/></SelectTrigger>
              <SelectContent>{LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Value (₹)</Label><Input type="number" value={form.value} onChange={e => setForm({...form, value: e.target.value})} disabled={readOnly}/></div>
          <div><Label>Mfg Date</Label><Input type="date" value={form.mfg_date} onChange={e => setForm({...form, mfg_date: e.target.value})} disabled={readOnly}/></div>
          <div><Label>Exp Date</Label><Input type="date" value={form.exp_date} onChange={e => setForm({...form, exp_date: e.target.value})} disabled={readOnly}/></div>
        </div>
        <DialogFooter>
          {readOnly ? <Button variant="outline" onClick={onClose}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} data-testid="fg-save">Save Changes</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FgAdjustDialog({ open, fg, onClose, onDone }) {
  const [delta, setDelta] = useState(0);
  const [kind, setKind] = useState("damage");
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setDelta(0); setKind("damage"); setReason(""); } }, [open]);

  const submit = async () => {
    try {
      await api.post(`/finished-goods/${fg.id}/adjust`, { delta: parseInt(delta), kind, reason });
      toast.success("Adjustment recorded");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Stock — {fg?.batch_no}</DialogTitle>
          <DialogDescription className="sr-only">Adjust FG stock</DialogDescription>
        </DialogHeader>
        <div className="text-sm text-muted-foreground mb-2">
          Current: <span className="font-semibold text-foreground mono">{fg?.qty}</span> pouches at <b>{fg?.location}</b>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Delta (± pouches)</Label>
            <Input type="number" value={delta} onChange={e => setDelta(e.target.value)} placeholder="-10 or +5" data-testid="fg-delta"/>
          </div>
          <div><Label>Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="fg-adj-kind"><SelectValue/></SelectTrigger>
              <SelectContent>{ADJ_KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Reason (required)</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Damage during handling, expiry write-off, physical count correction…" data-testid="fg-adj-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!reason || parseInt(delta) === 0} data-testid="fg-adj-save">Record Adjustment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FinishedGoods() {
  const { user } = useAuth();
  const canManage = ["admin", "dispatch", "production_manager", "store"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editFg, setEditFg] = useState(null);
  const [viewFg, setViewFg] = useState(null);
  const [adjFg, setAdjFg] = useState(null);

  const load = async () => {
    setItems((await api.get("/finished-goods", { params: { include_deleted: showDeleted } })).data);
  };
  useEffect(() => { load(); }, [showDeleted]);  // eslint-disable-line

  const del = async (fg) => {
    if (!window.confirm(`Archive FG lot ${fg.batch_no}? Historic ledger is preserved.`)) return;
    try { await api.delete(`/finished-goods/${fg.id}`); toast.success("Lot archived"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restore = async (fg) => {
    try { await api.post(`/finished-goods/${fg.id}/restore`); toast.success("Lot restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const totalQty = items.reduce((a, i) => a + (i.qty || 0), 0);
  const totalValue = items.reduce((a, i) => a + (i.value || 0), 0);
  const skuCount = new Set(items.map(i => i.sku_code)).size;

  return (
    <div data-testid="fg-page">
      <PageHeader subtitle="Warehouse" title="Finished Goods Inventory"
        actions={
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} data-testid="fg-show-deleted"/>
            Show archived
          </label>
        }
      />

      <div className="grid grid-cols-3 gap-4 mb-4">
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Total SKUs</div><div className="text-metric text-2xl">{skuCount}</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Total Pouches</div><div className="text-metric text-2xl">{totalQty.toLocaleString("en-IN")}</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-5"><div className="overline mb-2">Inventory Value</div><div className="text-metric text-2xl text-primary">₹{totalValue.toLocaleString("en-IN")}</div></CardContent></Card>
      </div>

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead><TableHead>SKU</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Reserved</TableHead>
                <TableHead>Mfg Date</TableHead><TableHead>Exp Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Value ₹</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(i => {
                const deleted = !!i.deleted_at;
                return (
                  <TableRow key={i.id} className={deleted ? "opacity-60" : ""} data-testid={`fg-row-${i.batch_no}`}>
                    <TableCell className="mono font-semibold">
                      {i.batch_no}
                      {deleted && <Badge variant="secondary" className="ml-1.5 uppercase text-[9px]">archived</Badge>}
                    </TableCell>
                    <TableCell><div className="text-sm">{i.sku_name}</div><div className="text-xs mono text-muted-foreground">{i.sku_code}</div></TableCell>
                    <TableCell className="text-right mono font-semibold">{i.qty}</TableCell>
                    <TableCell className="text-right mono text-xs text-muted-foreground">{i.reserved || 0}</TableCell>
                    <TableCell className="text-xs">{new Date(i.mfg_date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs">{new Date(i.exp_date).toLocaleDateString()}</TableCell>
                    <TableCell><Badge variant="secondary">{i.location}</Badge></TableCell>
                    <TableCell className="text-right mono">{i.value?.toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`fg-actions-${i.batch_no}`}>
                            <DotsThreeVertical size={16} weight="bold"/>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setViewFg(i)}>
                            <Eye size={14} className="mr-2"/>View
                          </DropdownMenuItem>
                          {canManage && !deleted && (
                            <>
                              <DropdownMenuItem onSelect={() => setEditFg(i)} data-testid={`fg-edit-${i.batch_no}`}>
                                <PencilSimple size={14} className="mr-2"/>Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => setAdjFg(i)} data-testid={`fg-adjust-${i.batch_no}`}>
                                <Scales size={14} className="mr-2"/>Adjust Stock
                              </DropdownMenuItem>
                              <DropdownMenuSeparator/>
                              <DropdownMenuItem className="text-red-600" onSelect={() => del(i)} data-testid={`fg-delete-${i.batch_no}`}>
                                <Trash size={14} className="mr-2"/>Archive Lot
                              </DropdownMenuItem>
                            </>
                          )}
                          {canManage && deleted && (
                            <DropdownMenuItem className="text-emerald-700" onSelect={() => restore(i)} data-testid={`fg-restore-${i.batch_no}`}>
                              <ArrowUUpLeft size={14} className="mr-2"/>Restore
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FgEditor open={!!editFg} fg={editFg} onClose={() => setEditFg(null)} onDone={load}/>
      <FgEditor open={!!viewFg} fg={viewFg} onClose={() => setViewFg(null)} readOnly/>
      <FgAdjustDialog open={!!adjFg} fg={adjFg} onClose={() => setAdjFg(null)} onDone={load}/>
    </div>
  );
}
